<c-container [fluid]="true" class="bank-reconciliation py-3">
  <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
    <div>
      <h4 class="mb-0">Bank Reconciliation</h4>
      <div class="text-medium-emphasis small" *ngIf="account() as acct">{{ acct.name }} · {{ acct.currency }}</div>
    </div>
  </div>

  <c-alert color="danger" *ngIf="error() as e" [dismissible]="true" (visibleChange)="error.set(null)">
    <ng-container [ngSwitch]="e">
      <span *ngSwitchCase="'load'">Could not load bank reconciliation data.</span>
      <span *ngSwitchCase="'upload'">The CSV file could not be imported. Check the format and try again.</span>
      <span *ngSwitchCase="'action'">The action could not be completed.</span>
      <span *ngSwitchDefault>{{ e }}</span>
    </ng-container>
  </c-alert>

  <!-- No bank account configured for this domain -->
  <c-card *ngIf="noAccount()" class="mb-3">
    <c-card-body>
      <p class="mb-0">No bank account is configured for this domain. Set the bank-transfer beneficiary
        details (env vars) to enable statement import and reconciliation.</p>
    </c-card-body>
  </c-card>

  <ng-container *ngIf="!noAccount()">
    <!-- Upload panel -->
    <c-card class="mb-3">
      <c-card-header>Import statement CSV</c-card-header>
      <c-card-body>
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <input type="file" accept=".csv,text/csv" class="form-control" style="max-width: 380px;"
                 (change)="onFileSelected($event)" [disabled]="uploadBusy()" />
          <button cButton color="primary" (click)="upload()" [disabled]="!selectedFile || uploadBusy()">
            <c-spinner *ngIf="uploadBusy()" size="sm" class="me-1"></c-spinner>
            Upload &amp; import
          </button>
          <span class="text-medium-emphasis small">Re-importing the same statement is safe — duplicate rows are skipped.</span>
        </div>

        <c-alert color="success" class="mt-3 mb-0" *ngIf="lastImport() as imp">
          Imported <strong>{{ imp.filename || 'file' }}</strong>:
          {{ imp.row_count }} rows · <strong>{{ imp.new_count }}</strong> new ·
          {{ imp.duplicate_count }} duplicates · <strong>{{ imp.matched_count }}</strong> auto-matched.
        </c-alert>

        <!-- Import history -->
        <div class="mt-3" *ngIf="imports().length">
          <div class="text-medium-emphasis small mb-1">Recent imports</div>
          <table cTable [hover]="true" [small]="true" class="mb-0">
            <thead>
              <tr>
                <th>Date</th><th>File</th><th>By</th>
                <th class="text-end">Rows</th><th class="text-end">New</th>
                <th class="text-end">Dup</th><th class="text-end">Matched</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let imp of imports()">
                <td>{{ imp.created_at | date:'short' }}</td>
                <td>{{ imp.filename || '—' }}</td>
                <td>{{ imp.imported_by || '—' }}</td>
                <td class="text-end">{{ imp.row_count }}</td>
                <td class="text-end">{{ imp.new_count }}</td>
                <td class="text-end">{{ imp.duplicate_count }}</td>
                <td class="text-end">{{ imp.matched_count }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </c-card-body>
    </c-card>

    <!-- Transactions -->
    <c-card>
      <c-card-header class="d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div class="btn-group btn-group-sm" role="group">
          <button *ngFor="let s of statuses" cButton
                  [color]="filters.status === s ? 'primary' : 'secondary'"
                  [variant]="filters.status === s ? undefined : 'outline'"
                  (click)="setStatus(s)">
            {{ s === '' ? 'All' : (s | titlecase) }}
          </button>
        </div>
        <div class="d-flex gap-2">
          <input cFormControl size="sm" style="max-width: 220px;" placeholder="Search description / check #"
                 [(ngModel)]="filters.q" (keyup.enter)="search()" />
          <button cButton size="sm" color="secondary" variant="outline" (click)="search()">Search</button>
        </div>
      </c-card-header>
      <c-card-body>
        <div class="text-center py-4" *ngIf="loading()">
          <c-spinner></c-spinner>
        </div>

        <div *ngIf="!loading()">
          <div class="text-medium-emphasis text-center py-4" *ngIf="!transactions().length">
            No transactions found.
          </div>

          <table cTable [hover]="true" [responsive]="true" *ngIf="transactions().length">
            <thead>
              <tr>
                <th>Date</th>
                <th>Description</th>
                <th class="text-end">Amount</th>
                <th>Status</th>
                <th>Matched booking</th>
                <th class="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let t of transactions()" [class.table-active]="t.reconciliation_status === 'matched'">
                <td class="text-nowrap">{{ t.transaction_date | date:'yyyy-MM-dd' }}</td>
                <td class="small" style="max-width: 420px;">
                  <div class="text-truncate" [title]="t.description">{{ t.description }}</div>
                  <span class="text-medium-emphasis" *ngIf="t.check_number">Check #{{ t.check_number }}</span>
                </td>
                <td class="text-end text-nowrap" [class.text-success]="t.incoming" [class.text-danger]="!t.incoming">
                  {{ t.amount }} {{ t.currency }}
                </td>
                <td>
                  <c-badge [color]="statusColor(t.reconciliation_status)">{{ t.reconciliation_status | titlecase }}</c-badge>
                  <div class="text-medium-emphasis small" *ngIf="t.match_method">{{ t.match_method }}</div>
                </td>
                <td class="small">
                  <ng-container *ngIf="t.matched_order as o">
                    <div><strong>#{{ o.order_id }}</strong> {{ o.reference }}</div>
                    <div class="text-medium-emphasis">{{ o.member }} · {{ o.amount }} {{ o.currency }}</div>
                  </ng-container>
                  <span class="text-medium-emphasis" *ngIf="!t.matched_order">—</span>
                </td>
                <td class="text-end text-nowrap">
                  <c-spinner *ngIf="busyId() === t.id" size="sm"></c-spinner>

                  <ng-container *ngIf="busyId() !== t.id">
                    <!-- unmatched incoming -->
                    <ng-container *ngIf="t.reconciliation_status === 'unmatched'">
                      <button *ngIf="t.incoming" cButton size="sm" color="primary" class="me-1" (click)="openMatch(t)">Match…</button>
                      <button cButton size="sm" color="secondary" variant="outline" (click)="ignore(t)">Ignore</button>
                    </ng-container>

                    <!-- matched, awaiting booking -->
                    <ng-container *ngIf="t.reconciliation_status === 'matched'">
                      <button cButton size="sm" color="success" class="me-1" (click)="book(t)">Book</button>
                      <button cButton size="sm" color="secondary" variant="outline" (click)="unmatch(t)">Unmatch</button>
                    </ng-container>

                    <!-- booked -->
                    <span *ngIf="t.reconciliation_status === 'booked'" class="text-medium-emphasis small">
                      Booked {{ t.booked_at | date:'yyyy-MM-dd' }}
                    </span>

                    <!-- ignored -->
                    <button *ngIf="t.reconciliation_status === 'ignored'" cButton size="sm" color="secondary" variant="outline" (click)="unignore(t)">Restore</button>
                  </ng-container>
                </td>
              </tr>
            </tbody>
          </table>

          <c-pagination *ngIf="pagination().total_pages > 1" class="mt-3 mb-0" aria-label="Transactions pages">
            <li cPageItem [disabled]="pagination().page === 1">
              <a cPageLink (click)="goToPage(pagination().page - 1)">‹</a>
            </li>
            <li cPageItem *ngFor="let p of pages()" [active]="p === pagination().page">
              <a cPageLink (click)="goToPage(p)">{{ p }}</a>
            </li>
            <li cPageItem [disabled]="pagination().page === pagination().total_pages">
              <a cPageLink (click)="goToPage(pagination().page + 1)">›</a>
            </li>
          </c-pagination>
        </div>
      </c-card-body>
    </c-card>
  </ng-container>
</c-container>

<!-- Match modal -->
<c-modal [visible]="matchOpen()" size="lg" (visibleChange)="onMatchVisibleChange($event)" [scrollable]="true">
  <c-modal-header>
    <h5 cModalTitle>Match transaction to a booking</h5>
  </c-modal-header>
  <c-modal-body>
    <div class="mb-3 small" *ngIf="matchTxn() as t">
      <div class="text-medium-emphasis">Transaction</div>
      <div><strong>{{ t.amount }} {{ t.currency }}</strong> · {{ t.transaction_date | date:'yyyy-MM-dd' }}</div>
      <div class="text-truncate" [title]="t.description">{{ t.description }}</div>
    </div>

    <div class="text-center py-3" *ngIf="matchLoading()"><c-spinner size="sm"></c-spinner></div>

    <ng-container *ngIf="!matchLoading()">
      <!-- Suggestions -->
      <div class="mb-3" *ngIf="suggestions().length">
        <div class="text-medium-emphasis small mb-1">Suggested matches</div>
        <div *ngFor="let s of suggestions()"
             class="d-flex justify-content-between align-items-center border rounded p-2 mb-2">
          <div class="small">
            <div>
              <c-badge [color]="s.confidence === 'strong' ? 'success' : 'warning'" class="me-1">{{ s.confidence }}</c-badge>
              <strong>#{{ s.order.order_id }}</strong> {{ s.order.reference }} · {{ s.order.member }}
            </div>
            <div class="text-medium-emphasis">
              {{ s.order.amount }} {{ s.order.currency }} ·
              ordered {{ s.order.created_at | date:'yyyy-MM-dd' }} · {{ s.reason }}
            </div>
          </div>
          <button cButton size="sm" color="primary" (click)="matchTo(s.order.order_id)">Match</button>
        </div>
      </div>

      <div class="text-medium-emphasis small my-2" *ngIf="!suggestions().length">
        No automatic suggestion. Pick a booking below.
      </div>

      <!-- All open bookings -->
      <div>
        <div class="text-medium-emphasis small mb-1">All open bookings</div>
        <div class="text-medium-emphasis small" *ngIf="!openBookings().length">There are no open bank-transfer bookings.</div>
        <table cTable [small]="true" [hover]="true" *ngIf="openBookings().length" class="mb-0">
          <thead>
            <tr><th>Order</th><th>Ordered</th><th>Reference</th><th>Member</th><th class="text-end">Amount</th><th></th></tr>
          </thead>
          <tbody>
            <tr *ngFor="let o of openBookings()">
              <td>#{{ o.order_id }}</td>
              <td class="text-nowrap">{{ o.created_at | date:'yyyy-MM-dd' }}</td>
              <td>{{ o.reference }}</td>
              <td class="small">{{ o.member }}<div class="text-medium-emphasis">{{ o.email }}</div></td>
              <td class="text-end text-nowrap">{{ o.amount }} {{ o.currency }}</td>
              <td class="text-end"><button cButton size="sm" color="primary" variant="outline" (click)="matchTo(o.order_id)">Match</button></td>
            </tr>
          </tbody>
        </table>
      </div>
    </ng-container>
  </c-modal-body>
  <c-modal-footer>
    <button cButton color="secondary" variant="outline" (click)="closeMatch()">Close</button>
  </c-modal-footer>
</c-modal>
